/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { getWeekRange } from "../../services/dateService";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useUserStats } from "../../store/useUserStats";
import { Tooltip } from "@material-tailwind/react";

// Extend dayjs with plugins
dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

interface Props {
  periodProp: "all" | "yearly" | "quarterly" | "monthly" | "weekly";
  yearProp?: number;
  quarterProp?: "Q1" | "Q2" | "Q3" | "Q4";
  monthProp?: number;
  weekProp?: Date;
  selectedFiltersProp?: string[];
  selectedStoresProp?: string[];
}

export const ActiveInactiveUsers = React.memo(
  ({
    periodProp = "all",
    yearProp = dayjs().year(),
    quarterProp = "Q1",
    monthProp = dayjs().month(),
    weekProp = new Date(),
    selectedFiltersProp = [],
    selectedStoresProp = [],
  }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const {
      activeInactiveUsers,
      getActiveInactiveUsers,
      isActiveInactiveUsersLoading,
    } = useUserStats();
    const [_, takeScreenshot] = useScreenshot();
    /** State */
    const [period, setPeriod] = useState<
      "all" | "yearly" | "quarterly" | "monthly" | "weekly"
    >(periodProp);
    const [year, setYear] = useState<number>(yearProp);
    const [quarter, setQuarter] = useState<"Q1" | "Q2" | "Q3" | "Q4">(
      quarterProp
    );
    const [month, setMonth] = useState<number>(monthProp);
    const [week, setWeek] = useState<Date>(weekProp);
    /** Constants */
    const { startOfWeek, endOfWeek } = getWeekRange(week);
    const currentYear = dayjs().year();

    /** Callbacks */
    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "amount_of_drops.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    const getLastWeek = () =>
      setWeek(new Date(week.setDate(week.getDate() - 7)));
    const getNextWeek = () =>
      setWeek(new Date(week.setDate(week.getDate() + 7)));

    /** Effects */
    useEffect(() => {
      if (period === "all")
        getActiveInactiveUsers({
          period,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "yearly")
        getActiveInactiveUsers({
          period,
          year,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "quarterly")
        getActiveInactiveUsers({
          period,
          year,
          quarter,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "monthly")
        getActiveInactiveUsers({
          period,
          year,
          month,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "weekly")
        getActiveInactiveUsers({
          period,
          year,
          month,
          week,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
    }, [
      period,
      year,
      quarter,
      month,
      week,
      selectedFiltersProp,
      selectedStoresProp,
    ]);

    useEffect(() => {
      setPeriod(periodProp);
      setYear(yearProp);
      setQuarter(quarterProp);
      setMonth(monthProp);
      setWeek(weekProp);
    }, [periodProp, yearProp, quarterProp, monthProp, weekProp]);

    const monthlyTickAmount = useMemo(() => {
      if (!activeInactiveUsers) return 11;
      // @ts-ignore
      return Object.values(activeInactiveUsers)?.[0]?.data?.length - 1 || 11;
    }, [activeInactiveUsers]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div className="flex items-center mb-2 sm:mb-12">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Non cumulative new users in time period
            </p>
            <Tooltip
              content={
                <div className="w-80 font-satoshi">
                  <p className="font-satoshi font-semibold mb-2">
                    New Active Users:
                  </p>
                  <p className="font-satoshi text-sm mb-3">
                    Users who became active this period - either users who did
                    their second drop in this period, or users who were inactive
                    and became active again in this period.
                  </p>
                  <p className="font-satoshi font-semibold mb-2">
                    New Inactive Users:
                  </p>
                  <p className="font-satoshi text-sm">
                    Users who were active and became inactive in this period.
                  </p>
                </div>
              }
              className="bg-gray-900"
              placement="bottom"
            >
              <div className="ml-2 w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center cursor-help">
                <span className="text-xs font-satoshi font-bold text-gray-700">
                  i
                </span>
              </div>
            </Tooltip>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            {period === "weekly" ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
                <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
                </div>
                <PrimaryButton
                  size="sm"
                  disabled={endOfWeek > new Date()}
                  onClick={getNextWeek}
                  label="Next"
                />
              </div>
            ) : null}
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "all"}
                  onClick={() => setPeriod && setPeriod("all")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  All
                </MenuItem>
                <MenuItem
                  disabled={period === "weekly"}
                  onClick={() => setPeriod && setPeriod("weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "monthly"}
                  onClick={() => setPeriod && setPeriod("monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "quarterly"}
                  onClick={() => setPeriod && setPeriod("quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={period === "yearly"}
                  onClick={() => setPeriod && setPeriod("yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
            {period !== "all" && period !== "weekly" ? (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {year}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {Array.from(
                    { length: currentYear - 2020 },
                    (_, i) => currentYear - i
                  ).map((y) => (
                    <MenuItem
                      key={y}
                      onClick={() => setYear && setYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                      disabled={year === y}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ) : null}
            {period === "monthly" ? (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {dayjs().month(month).format("MMMM")}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((m) => (
                    <MenuItem
                      key={m}
                      onClick={() => setMonth && setMonth(m)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                      disabled={month === m}
                    >
                      {dayjs().month(m).format("MMMM")}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ) : null}
            {period === "quarterly" ? (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {quarter}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                    <MenuItem
                      key={q}
                      onClick={() =>
                        setQuarter && setQuarter(q as "Q1" | "Q2" | "Q3" | "Q4")
                      }
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                      disabled={quarter === q}
                    >
                      {q}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ) : null}
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef}>
          {isActiveInactiveUsersLoading ? (
            <div className="flex items-center justify-center h-[300px] w-full">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          ) : (
            <>
              {activeInactiveUsers && period === "all" ? (
                <Chart
                  // @ts-ignore
                  options={{
                    ...options,
                    xaxis: {
                      tickAmount: activeInactiveUsers?.[0]?.data?.length - 1,
                      labels: {
                        // @ts-ignore
                        formatter: function (value: number) {
                          return dayjs(value).format("MMM YYYY");
                        },
                      },
                    },
                  }}
                  series={activeInactiveUsers}
                  type="bar"
                  height={300}
                />
              ) : null}
              {activeInactiveUsers && period === "weekly" ? (
                <Chart
                  // @ts-ignore
                  options={{
                    ...options,
                    xaxis: {
                      type: "datetime",
                      tickAmount: 6,
                      labels: {
                        // @ts-ignore
                        formatter: function (value: number) {
                          return dayjs(value).format("ddd");
                        },
                      },
                    },
                  }}
                  series={activeInactiveUsers}
                  type="bar"
                  height={300}
                />
              ) : null}
              {activeInactiveUsers && period === "monthly" ? (
                <Chart
                  options={{
                    ...options,
                    xaxis: {
                      // Only show 1 tick when there's only one month
                      // @ts-ignore
                      tickAmount: monthlyTickAmount,
                      labels: {
                        // @ts-ignore
                        formatter: function (value: number) {
                          return dayjs(value).format("DD MMM");
                        },
                      },
                    },
                  }}
                  series={activeInactiveUsers}
                  type="bar"
                  height={300}
                />
              ) : null}
              {activeInactiveUsers && period === "quarterly" ? (
                <Chart
                  // @ts-ignore
                  options={{
                    ...options,
                    xaxis: {
                      tickAmount: 2,
                      labels: {
                        // @ts-ignore
                        formatter: function (value: number) {
                          return dayjs(value).format("MMM");
                        },
                      },
                    },
                  }}
                  series={activeInactiveUsers}
                  type="bar"
                  height={300}
                />
              ) : null}
              {activeInactiveUsers && period === "yearly" ? (
                <Chart
                  // @ts-ignore
                  options={{
                    ...options,
                    xaxis: {
                      tickAmount: activeInactiveUsers?.[0]?.data?.length - 1,
                      labels: {
                        // @ts-ignore
                        formatter: function (value: number) {
                          return dayjs(value).format("MMM");
                        },
                      },
                    },
                  }}
                  series={activeInactiveUsers}
                  type="bar"
                  height={300}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "bar",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
    stacked: false,
  },
  plotOptions: {
    bar: {
      columnWidth: "60%",
      dataLabels: {
        position: "top",
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  tooltip: {
    theme: "dark",
  },
  colors: [
    "#FFB323", // Yellow
    "#8A2BE2", // BlueViolet
    "#00CED1", // DarkTurquoise
    "#23C16B", // Green
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
